"use client";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  Box,
  Button,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  floatingAmountSx,
  floatingCartSearchMobileSx,
  floatingSaveSx,
  viewCartButtonSx,
} from "./floatingCartShared";
import Slider from "react-slick";
import { AddToCartService, MainCart } from "@/app/services/cart";
import Image from "next/image";
import { useCurrencyCode } from "../helper-functions/CurrencySymbol";
import "./index.css";
import { useRouter } from "next/navigation";
import { Loader } from "../MainLoader/Loader";

const AllCartsSlideSearch = () => {
  const addToCartService = useMemo(() => new AddToCartService(), []);
  const [allCarts, setAllCarts] = useState<MainCart | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [portalReady, setPortalReady] = useState(false);
  const currencyCode = useCurrencyCode();
  const router = useRouter();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const settings = useMemo(
    () => ({
      dots: isSmallScreen ? true : false,
      infinite: true,
      autoplay: true,
      autoplaySpeed: 10000,
      arrows: isSmallScreen ? false : true,
      speed: 2000,
      slidesToShow: 1,
      slidesToScroll: 1,
      className: isSmallScreen
        ? "myCustomCarouselCartSearchMobileView"
        : "myCustomCarouselCart",
    }),
    [isSmallScreen]
  );

  const getAllCarts = useCallback(() => {
    const mainCart = addToCartService.getCart();
    if (!mainCart) {
      // console.log("No cart found");
      return;
    }
    setAllCarts(mainCart);
  }, [addToCartService]);

  useEffect(() => {
    getAllCarts();
  }, [getAllCarts]);

  useEffect(() => {
    setPortalReady(true);
  }, []);

  useEffect(() => {
    const intr = setInterval(() => {
      getAllCarts();
    }, 1000);
    return () => {
      clearInterval(intr);
    };
  }, [getAllCarts]);

  const NavigateViewCart = (item: any) => {
    setLoading(true);
    router.push("/cart");
    localStorage.setItem("cid", btoa(JSON.stringify(item?.cid)));
  };

  const inner = (
    <>
      {loading && <Loader />}
      {allCarts && allCarts?.cartItemRequestsById?.length > 0 ? (
        allCarts?.cartItemRequestsById?.length == 1 ? (
          <Box sx={floatingCartSearchMobileSx}>
            {allCarts?.cartItemRequestsById?.map((item: any) => (
              <Box
                key={item.cid}
                sx={{
                  display: "flex !important",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: isSmallScreen ? "2px 8px" : "6px 32px",
                  flexDirection: "row",
                }}
              >
                <Box sx={{ marginRight: "0.5rem" }}>
                  <Image
                    height={200}
                    width={200}
                    alt="Cart item"
                    src={item?.cartItemRequests[0]?.ImageUrl}
                    unoptimized
                    style={{
                      height: isSmallScreen ? "50px" : "55px",
                      width: isSmallScreen ? "50px" : "55px",
                      minWidth: isSmallScreen ? "50px" : "55px",
                      minHeight: isSmallScreen ? "50px" : "55px",
                      objectFit: "cover",
                      borderRadius: "50%",
                    }}
                  />
                </Box>
                <Box>
                  <Typography
                    sx={{
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "normal",
                      height: "40px",
                      alignContent: "center",
                    }}
                    variant="caption"
                  >
                    {item?.cartItemRequests
                      ?.map((service: any) => service.serviceName)
                      .join(", ")}
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "start",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="caption" sx={floatingAmountSx}>
                      {currencyCode}
                      {item?.totalAmount}
                    </Typography>
                    &nbsp; &nbsp;
                    <Typography
                      sx={{
                        textDecoration: "line-through",
                        color: "gray",
                      }}
                      variant="caption"
                    >
                      {currencyCode}
                      {item?.totalMrp}
                    </Typography>
                    &nbsp; &nbsp;
                    <Typography variant="caption" sx={floatingSaveSx}>
                      Save {currencyCode}
                      {item?.totalDiscount}
                    </Typography>
                  </Box>
                </Box>
                &nbsp; &nbsp; &nbsp;
                <Box>
                  <Button
                    variant="outlined"
                    sx={viewCartButtonSx}
                    onClick={() => NavigateViewCart(item)}
                  >
                    View Cart
                  </Button>
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <Box sx={floatingCartSearchMobileSx}>
            <Slider {...settings}>
              {allCarts?.cartItemRequestsById?.map((item: any) => (
                <Box
                  key={item.cid}
                  sx={{
                    display: "flex !important",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: isSmallScreen ? "2px 8px" : "6px 32px",
                    flexDirection: "row",
                  }}
                >
                  <Box sx={{ marginRight: "0.5rem" }}>
                    <Image
                      height={200}
                      width={200}
                      alt="Cart item"
                      src={item?.cartItemRequests[0]?.ImageUrl}
                      unoptimized
                      style={{
                        height: isSmallScreen ? "50px" : "55px",
                        width: isSmallScreen ? "50px" : "55px",
                        minWidth: isSmallScreen ? "50px" : "55px",
                        minHeight: isSmallScreen ? "50px" : "55px",
                        objectFit: "cover",
                        borderRadius: "50%",
                      }}
                    />
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "normal",
                        height: "40px",
                        alignContent: "center",
                      }}
                      variant="caption"
                    >
                      {item?.cartItemRequests
                        ?.map((service: any) => service.serviceName)
                        .join(", ")}
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "start",
                        alignItems: "center",
                      }}
                    >
                      <Typography variant="caption" sx={floatingAmountSx}>
                        {currencyCode}
                        {item?.totalAmount}
                      </Typography>
                      &nbsp; &nbsp;
                      <Typography
                        sx={{
                          textDecoration: "line-through",
                          color: "gray",
                        }}
                        variant="caption"
                      >
                        {currencyCode}
                        {item?.totalMrp}
                      </Typography>
                      &nbsp; &nbsp;
                      <Typography variant="caption" sx={floatingSaveSx}>
                        Save {currencyCode}
                        {item?.totalDiscount}
                      </Typography>
                    </Box>
                  </Box>
                  &nbsp; &nbsp; &nbsp;
                  <Box>
                    <Button
                      variant="outlined"
                      sx={viewCartButtonSx}
                      onClick={() => NavigateViewCart(item)}
                    >
                      View Cart
                    </Button>
                  </Box>
                </Box>
              ))}
            </Slider>
          </Box>
        )
      ) : (
        ""
      )}
    </>
  );

  if (!portalReady || typeof document === "undefined") {
    return null;
  }

  return createPortal(inner, document.body);
};

export default AllCartsSlideSearch;
